import { ModalTitleIcons } from '@components/common/Modal';
import CloseConfirmationModal from '@components/common/modals/CloseConfirmationModal';
import ClosePositionModal from '@components/common/modals/ClosePositionModal';
import LegalDisclaimerModal from '@components/common/modals/LegalDisclaimerModal';
import ProcessingModal from '@components/common/modals/ProcessingModal';
import RouteAndFeesModal from '@components/common/modals/RouteAndFeesModal';
import UnavailableClosingPositionModal from '@components/common/modals/UnavailableClosingPositionModal/UnavailableClosingPositionModal';
import ViewNftModal from '@components/common/modals/ViewNftModal';
import WrongNetworkModal from '@components/common/modals/WrongNetworkModal';
import { dappStore } from '@components/store';
import {
  getShowIsRouteAndFeesModal,
  getShowLegalDisclaimerModal,
  useIsCloseConfirmationModal,
  useIsClosePositionModal,
  useIsClosingPositionModal,
  useIsProcessingModalOpen,
  useIsViewNft,
  useIsWrongNetwork,
  useActivePosition,
  useShowUnavailableClosingPositionModal,
} from '@components/store/getters';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';

export const useModal = () => {
  const viewNftModalOpened = useIsViewNft();
  const wrongNetworkModalOpened = useIsWrongNetwork();
  const processingModalOpened = useIsProcessingModalOpen();
  const showLegalDisclaimerModal = getShowLegalDisclaimerModal();
  const showRouteAndFeesModal = getShowIsRouteAndFeesModal();
  const activePosition = useActivePosition();
  const closePositionModalOpened = useIsClosePositionModal();
  const closeConfimationModalOpened = useIsCloseConfirmationModal();
  const closingPositionModalOpened = useIsClosingPositionModal();
  const unavailableClosingPositionModalOpened = useShowUnavailableClosingPositionModal();
  const { pathname } = useRouter();

  // Always check if we need to show the legal disclaimer modal
  useEffect(() => {
    const showLegalDisclaimer = localStorage.getItem('showLegalDisclaimer');
    if (!showLegalDisclaimer) {
      localStorage.setItem('showLegalDisclaimer', 'true');
      dappStore.setState({ showLegalDisclaimerModal: true });
    } else {
      dappStore.setState({ showLegalDisclaimerModal: JSON.parse(showLegalDisclaimer) });
    }
  }, []);

  // Render the modals
  const modalComponent = useMemo(() => {
    if (showRouteAndFeesModal) return <RouteAndFeesModal />;
    if (wrongNetworkModalOpened) return <WrongNetworkModal />;
    if (showLegalDisclaimerModal) return <LegalDisclaimerModal />;
    if (viewNftModalOpened) {
      if (activePosition)
        return <ViewNftModal confirmationMode={pathname.includes('open-position')} activePosition={activePosition} />;
    }
    if (processingModalOpened) return <ProcessingModal />;
    if (closePositionModalOpened) {
      if (activePosition) return <ClosePositionModal activePosition={activePosition} />;
    }
    if (closeConfimationModalOpened) return <CloseConfirmationModal />;
    if (unavailableClosingPositionModalOpened) return <UnavailableClosingPositionModal />;
  }, [
    processingModalOpened,
    wrongNetworkModalOpened,
    viewNftModalOpened,
    activePosition,
    showLegalDisclaimerModal,
    showRouteAndFeesModal,
    closePositionModalOpened,
    closeConfimationModalOpened,
    unavailableClosingPositionModalOpened,
    pathname,
  ]);

  const modalTitle = useMemo(() => {
    if (wrongNetworkModalOpened) return 'Change your network';
    if (showLegalDisclaimerModal) return 'Legal Disclaimer';
    if (viewNftModalOpened) {
      if (pathname.includes('open-position')) return 'Confirmation';
      return 'Your Position Details';
    }
    if (processingModalOpened) return 'Processing Transaction';
    if (showRouteAndFeesModal) return 'Route & Fee Details';
    if (closePositionModalOpened) return 'Are you sure you want to close this position?';
    if (unavailableClosingPositionModalOpened) return 'Sorry, closing a position is temporarily unavailable.';
    if (closingPositionModalOpened) return 'Closing Position...';
    return 'Confirmation';
  }, [
    processingModalOpened,
    wrongNetworkModalOpened,
    viewNftModalOpened,
    showLegalDisclaimerModal,
    showRouteAndFeesModal,
    closePositionModalOpened,
    unavailableClosingPositionModalOpened,
    closingPositionModalOpened,
    pathname,
  ]);

  function closeModal() {
    if (viewNftModalOpened) {
      dappStore.setState({ isViewNft: false });
      return;
    }
    if (processingModalOpened) return dappStore.setState({ isProcessingModalOpen: false });
    if (showRouteAndFeesModal) return dappStore.setState({ showRouteAndFeesModal: false });
    if (closePositionModalOpened) return dappStore.setState({ isClosePositionModal: false });
    if (unavailableClosingPositionModalOpened)
      return dappStore.setState({ showUnavailableClosingPositionModal: false });
    if (closeConfimationModalOpened) return dappStore.setState({ isCloseConfirmationModal: false });
    if (unavailableClosingPositionModalOpened)
      return dappStore.setState({ showUnavailableClosingPositionModal: false });
  }

  const hideCloseButton = useMemo(() => {
    return wrongNetworkModalOpened || showLegalDisclaimerModal;
  }, [wrongNetworkModalOpened, showLegalDisclaimerModal]);

  const showLoader = useMemo(() => {
    return processingModalOpened || closingPositionModalOpened;
  }, [processingModalOpened, closingPositionModalOpened]);

  const titleIconName: ModalTitleIcons = useMemo(() => {
    if (unavailableClosingPositionModalOpened) return 'alert';
  }, [unavailableClosingPositionModalOpened]);

  const isModalOpen = useMemo(() => {
    return (
      (viewNftModalOpened && activePosition) ||
      wrongNetworkModalOpened ||
      (closePositionModalOpened && activePosition) ||
      showLegalDisclaimerModal ||
      processingModalOpened ||
      showRouteAndFeesModal ||
      unavailableClosingPositionModalOpened ||
      closeConfimationModalOpened ||
      wrongNetworkModalOpened ||
      closingPositionModalOpened
    );
  }, [
    processingModalOpened,
    wrongNetworkModalOpened,
    viewNftModalOpened,
    activePosition,
    showLegalDisclaimerModal,
    showRouteAndFeesModal,
    unavailableClosingPositionModalOpened,
    closeConfimationModalOpened,
    closingPositionModalOpened,
    closePositionModalOpened,
  ]);

  return {
    modalComponent,
    modalTitle,
    closeModal,
    isModalOpen,
    hideCloseButton,
    showLoader,
    titleIconName,
  };
};
